import { useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';

gsap.registerPlugin(ScrollTrigger, SplitText);

interface TitleRevealProps {
  as?: 'h1' | 'h2' | 'h3';
  className?: string;
  children: ReactNode;
}

const prefersReduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Editorial line-mask title reveal. Each wrapped line rises 24px from its own
 * overflow mask. Plays once per mount. Hidden state is applied only from JS
 * (before paint) and always cleared on failure/cleanup, so text never stays hidden.
 * For dynamic text, remount via `key` instead of changing children in place.
 */
export default function TitleReveal({ as: Tag = 'h2', className = '', children }: TitleRevealProps) {
  const ref = useRef<HTMLHeadingElement>(null);

  // Hide before first paint to avoid a flash; skipped for reduced motion.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || prefersReduced()) return undefined;
    el.style.visibility = 'hidden';
    return () => {
      el.style.visibility = '';
    };
  }, []);

  // useEffect so the Hero pin (created in its own effect) exists before we measure.
  useEffect(() => {
    const el = ref.current;
    if (!el || prefersReduced()) return undefined;

    let split: SplitText | null = null;
    let tween: gsap.core.Tween | null = null;

    try {
      split = SplitText.create(el, {
        type: 'lines',
        mask: 'lines',
        autoSplit: true,
        onSplit(self) {
          // Room for descenders inside each mask without changing line box height.
          gsap.set(self.masks, { paddingBottom: '0.14em', marginBottom: '-0.14em' });
          el.style.visibility = '';
          tween = gsap.from(self.lines, {
            y: 24,
            autoAlpha: 0,
            duration: 0.8,
            ease: 'power3.out',
            stagger: 0.1,
            scrollTrigger: {
              trigger: el,
              start: 'top 88%',
              once: true,
              refreshPriority: -1,
            },
          });
          return tween;
        },
      });
    } catch {
      el.style.visibility = '';
    }

    return () => {
      tween?.scrollTrigger?.kill();
      tween?.kill();
      split?.revert();
      el.style.visibility = '';
    };
  }, []);

  return (
    <Tag ref={ref} className={className}>
      {children}
    </Tag>
  );
}